import { ComboBoxInput } from "@/components/shared/form/combo-box-input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { LoadingSpinner } from "@/components/ui/spinner";
import { TypographyH3 } from "@/components/ui/typography";
import type { BankAccountWithConnectionId } from "@/lib/payment/types";
import { formatIban } from "@/lib/utils";
import type { TranslationKey } from "@/localisation/types";
import type { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CreditCard } from "lucide-react";
import { useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  bankAccountId: z.string().min(1).max(255),
});

export type FormValues = z.infer<typeof formSchema>;

type Props = {
  onConfirm: (
    bankAccountId: string,
    bankConnectionId: string,
  ) => Promise<void> | void;
  onBack: () => void;
  bankAccounts: BankAccountWithConnectionId[];
  totalPayments: number;
};

export const PaymentAccountSelectionComponent = ({
  onConfirm,
  onBack,
  bankAccounts,
  totalPayments,
}: Props) => {
  const { t } = useTranslation();
  const [error, setError] = useState<ApolloError | null>(null);

  const defaultBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.defaultAccount,
  );

  const form = useForm<FormValues>({
    values: {
      bankAccountId: defaultBankAccount?.bankAccountId ?? "",
    },
    resolver: zodResolver(formSchema),
  });

  const handleSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      const selectedAccount = bankAccounts.find(
        (account) => account.bankAccountId === values.bankAccountId,
      );
      if (selectedAccount) {
        await onConfirm(
          selectedAccount.bankAccountId,
          selectedAccount.bankConnectionId,
        );
      }
    } catch (error) {
      setError(error as ApolloError);
    }
  };

  const options = useMemo(() => {
    return bankAccounts.map((bankAccount) => ({
      label: bankAccount.connectionName
        ? `${bankAccount.connectionName} - ${bankAccount.name}`
        : bankAccount.name,
      value: bankAccount.bankAccountId,
      subLabel: formatIban(bankAccount.iban),
      alternativeLabels: [],
    }));
  }, [bankAccounts]);

  const currentBankAccountId = form.watch("bankAccountId");

  const maxCollectivePayments = useMemo(() => {
    const bankAccount = bankAccounts.find(
      (bankAccount) => bankAccount.bankAccountId === currentBankAccountId,
    );

    return bankAccount?.maxCollectivePayments ?? 0;
  }, [bankAccounts, currentBankAccountId]);

  const isMaxPaymentsReached = useMemo(() => {
    return totalPayments > maxCollectivePayments;
  }, [totalPayments, maxCollectivePayments]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="container max-w-3xl mx-auto">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TypographyH3>{t("payment_steps.step_2.title")}</TypographyH3>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <ComboBoxInput
                name="bankAccountId"
                label={t("account")}
                options={options}
                placeholder={t("select_bank_account")}
                icon={CreditCard}
              />
            </div>
            {isMaxPaymentsReached && (
              <MaxPaymentWarning
                maxCollectivePayments={maxCollectivePayments}
              />
            )}
            {error && <PaymentInitiationError error={error} />}
            <div className="flex flex-row justify-center gap-2">
              <Button onClick={onBack} variant="outline">
                {t("back")}
              </Button>
              <Button
                type="submit"
                className="gap-1"
                disabled={
                  !form.formState.isValid ||
                  form.formState.isSubmitting ||
                  isMaxPaymentsReached
                }
              >
                {form.formState.isSubmitting && <LoadingSpinner />}
                {t("confirm_and_continue")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};

const MaxPaymentWarning = ({
  maxCollectivePayments,
}: {
  maxCollectivePayments: number;
}) => {
  const { t } = useTranslation();

  return (
    <Alert variant="warning">
      <div className="flex flex-row gap-2 items-start">
        <AlertCircle className="h-6 w-6" />
        <AlertDescription className="whitespace-pre-line">
          {t("bank_connections.max_payments.description", {
            x: maxCollectivePayments,
          })}
        </AlertDescription>
      </div>
    </Alert>
  );
};

const PaymentInitiationError = ({ error }: { error: ApolloError }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      {error.graphQLErrors.map((error, index) => (
        <Alert
          variant="warning"
          key={`${error.message}-${error.extensions?.code}-${index}`}
        >
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {t("open_banking.error.payment_initiation.title")}
          </AlertTitle>
          <AlertDescription className="whitespace-pre-line">
            {
              t(
                `open_banking.error.payment_initiation.${error.extensions?.code}` as TranslationKey,
              ) as string
            }
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
};
